import { useEffect, useState } from 'react';

import Fieldset from '@rewardopl/react-ui/fieldset';
import InputField from '@rewardopl/react-ui/input_field';
import SelectField from '@rewardopl/react-ui/select_field';

import { PostcodeAndCityWrapper } from './address_field.styles';

import RewardoAi from '../common/rewardo_ai';

import { COUNTRIES, COUNTRY_CODES, COUNTRY_NAMES } from '../../constants';

import type { Address } from '@rewardopl/types/helpers/address';

type AddressFieldProps = {
  label?: React.ReactNode;
  name: string;
  onChange: (value: Partial<Address> | undefined) => void;
  required?: boolean;
  value?: Partial<Address>;
};

const countryOptions: { label: string; value: string }[] = (
  Object.keys(COUNTRIES) as (keyof typeof COUNTRIES)[]
).map((key) => ({
  label: COUNTRY_NAMES[key],
  value: COUNTRY_CODES[key],
}));

const countryPostcodeCityMap: Map<
  (typeof COUNTRY_CODES)[keyof typeof COUNTRY_CODES],
  Map<string, string>
> = new Map([
  [
    COUNTRY_CODES.POLAND,
    new Map([
      ['00', 'Warszawa'],
      ['01', 'Warszawa'],
      ['02', 'Warszawa'],
      ['03', 'Warszawa'],
      ['04', 'Warszawa'],
      ['10', 'Olsztyn'],
      ['15', 'Białystok'],
      ['20', 'Lublin'],
      ['25', 'Kielce'],
      ['30', 'Kraków'],
      ['31', 'Kraków'],
      ['35', 'Rzeszów'],
      ['40', 'Katowice'],
      ['45', 'Opole'],
      ['50', 'Wrocław'],
      ['51', 'Wrocław'],
      ['52', 'Wrocław'],
      ['53', 'Wrocław'],
      ['54', 'Wrocław'],
      ['60', 'Poznań'],
      ['61', 'Poznań'],
      ['65', 'Zielona Góra'],
      ['70', 'Szczecin'],
      ['71', 'Szczecin'],
      ['75', 'Koszalin'],
      ['80', 'Gdańsk'],
      ['85', 'Bydgoszcz'],
      ['90', 'Łódź'],
      ['91', 'Łódź'],
      ['92', 'Łódź'],
      ['93', 'Łódź'],
      ['94', 'Łódź'],
    ]),
  ],
]);

export default function AddressField({
  label,
  name,
  onChange,
  required = true,
  value,
}: AddressFieldProps) {
  const [address, setAddress] = useState(value?.address);
  const [postcode, setPostcode] = useState(value?.postcode);
  const [city, setCity] = useState(value?.city);
  const [country, setCountry] = useState(value?.country ?? 'PL');
  const [state, setState] = useState(value?.state);
  const [isGuessedCity, setIsGuessedCity] = useState(false);

  useEffect(() => {
    if (!value) {
      return;
    }

    setAddress(value.address);
    setPostcode(value.postcode);
    setCity(value.city);
    setCountry(value.country ?? 'PL');
    setState(value.state);
  }, [value]);

  const postalCodePattern = (() => {
    switch (country) {
      case COUNTRY_CODES.GERMANY:
        return '\\d{5}';
      case COUNTRY_CODES.POLAND:
        return '\\d{2}-\\d{3}';
      case COUNTRY_CODES.UNITED_KINGDOM:
        return '[A-Z]{1,2}\\d{1,2}[A-Z]? \\d[A-Z]{2}';
      case COUNTRY_CODES.UNITED_STATES:
        return '\\d{5}(-\\d{4})?';
      default:
        return undefined;
    }
  })();

  const postalCodePlaceholder = (() => {
    switch (country) {
      case COUNTRY_CODES.GERMANY:
        return '00000';
      case COUNTRY_CODES.POLAND:
        return '00-000';
      case COUNTRY_CODES.UNITED_KINGDOM:
        return 'AA1A 1AA';
      case COUNTRY_CODES.UNITED_STATES:
        return '00000';
      default:
        return undefined;
    }
  })();

  function onChangeAddress(nextAddress: string | undefined) {
    setAddress(nextAddress);

    onChange({
      address: nextAddress,
      postcode,
      city,
      country,
      state,
    });
  }

  function onChangePostcode(nextPostcode: string | undefined) {
    setPostcode(nextPostcode);

    const isValidPostcode = nextPostcode && new RegExp(`^${postalCodePattern}$`).test(nextPostcode);

    let nextCity = city;

    if (isValidPostcode) {
      const postcodeCityMap = countryPostcodeCityMap.get(
        country as (typeof COUNTRY_CODES)[keyof typeof COUNTRY_CODES],
      );

      if (postcodeCityMap) {
        const maybeNextCity = postcodeCityMap.get(nextPostcode.slice(0, 2));

        if (maybeNextCity) {
          setCity(maybeNextCity);

          setIsGuessedCity(true);

          nextCity = maybeNextCity;
        }
      }
    }

    onChange({
      address,
      postcode: nextPostcode,
      city: nextCity,
      country,
      state,
    });
  }

  function onChangeCity(nextCity: string | undefined) {
    setCity(nextCity);

    setIsGuessedCity(false);

    onChange({
      address,
      postcode,
      city: nextCity,
      country,
      state,
    });
  }

  function onChangeState(nextState: string | undefined) {
    setState(nextState);

    onChange({
      address,
      postcode,
      city,
      country,
      state: nextState,
    });
  }

  function onChangeCountry(nextCountry: string) {
    setCountry(nextCountry);

    onChange({
      address,
      postcode,
      city,
      country: nextCountry,
      state,
    });
  }

  const postcodeField = (
    <InputField
      autoComplete="postal-code"
      hideOptional
      label="Postcode"
      name={`${name}[postcode]`}
      onChange={onChangePostcode}
      pattern={postalCodePattern}
      placeholder={postalCodePlaceholder}
      required={required}
      value={postcode}
    />
  );

  const cityField = (
    <InputField
      autoComplete="address-level2"
      hideOptional
      hint={isGuessedCity ? <RewardoAi /> : null}
      label="City"
      name={`${name}[city]`}
      onChange={onChangeCity}
      required={required}
      value={city}
    />
  );

  return (
    <Fieldset label={label} required={required}>
      <SelectField
        autoComplete="country"
        hideOptional
        label="Country"
        name={`${name}[country]`}
        onChange={onChangeCountry}
        options={countryOptions}
        required={required}
        value={country}
      />
      <InputField
        autoComplete="street-address"
        hideOptional
        label="Street address"
        name={`${name}[address]`}
        onChange={onChangeAddress}
        required={required}
        value={address}
      />
      <PostcodeAndCityWrapper>
        {country === COUNTRY_CODES.UNITED_KINGDOM ? (
          <>
            {cityField}
            {postcodeField}
          </>
        ) : country === COUNTRY_CODES.UNITED_STATES ? (
          <>
            {cityField}
            <InputField
              autoComplete="address-level1"
              hideOptional
              label="State"
              name={`${name}[state]`}
              onChange={onChangeState}
              placeholder="CA"
              required={required}
              value={state}
            />
            {postcodeField}
          </>
        ) : (
          <>
            {postcodeField}
            {cityField}
          </>
        )}
      </PostcodeAndCityWrapper>
    </Fieldset>
  );
}
